import React, { useRef, useState } from 'react';
import { Download, FileText, Loader2, Paperclip, Trash2, Upload } from 'lucide-react';
import { deleteVerbaleAllegato, uploadVerbaleAllegato } from '../../services/representativeService';
import { useToast } from '../../contexts/ToastContext';
import ConfirmDialog from '../ConfirmDialog';

export interface AllegatoVerbale {
  _id: string;
  filename: string;
  path: string;
  mimetype?: string;
  size?: number;
  uploadedAt?: string;
}

interface VerbaleAllegatiProps {
  eventId: string;
  allegati: AllegatoVerbale[];
  onCambio?: (allegati: AllegatoVerbale[]) => void;
  /** Lettori: elenco da scaricare, senza aggiunte né eliminazioni. */
  solaLettura?: boolean;
}

const MAX_BYTES = 25 * 1024 * 1024;
const ACCETTATI = '.pdf,.docx,.xlsx,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/jpeg,image/png,image/webp,image/heic';
const UPLOADS_BASE = process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || 'http://localhost:5000';

const misura = (bytes?: number) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Allegati del verbale (fino a 25 MB: PDF, Word, Excel, foto). */
const VerbaleAllegati: React.FC<VerbaleAllegatiProps> = ({ eventId, allegati, onCambio, solaLettura }) => {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [carico, setCarico] = useState(false);
  const [daTogliere, setDaTogliere] = useState<AllegatoVerbale | null>(null);

  if (solaLettura && allegati.length === 0) return null;

  const carica = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error('File troppo grande', 'Il limite è 25 MB per allegato.');
      return;
    }
    setCarico(true);
    try {
      const res = await uploadVerbaleAllegato(eventId, file);
      if (res?.success && res.allegato) {
        onCambio?.([...allegati, res.allegato]);
        toast.success('Allegato caricato');
      } else {
        toast.error('Allegato non caricato');
      }
    } catch (err: any) {
      toast.error('Allegato non caricato', err?.response?.data?.error || undefined);
    } finally {
      setCarico(false);
    }
  };

  const togli = async (allegato: AllegatoVerbale) => {
    try {
      await deleteVerbaleAllegato(eventId, allegato._id);
      onCambio?.(allegati.filter((a) => a._id !== allegato._id));
      toast.success('Allegato rimosso');
    } catch {
      toast.error('Allegato non rimosso, riprova');
    }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800" aria-label="Allegati del verbale">
      <div className="mb-2 flex items-center gap-2">
        <Paperclip className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
        <span className="text-sm font-semibold text-slate-900 dark:text-white">Allegati</span>
        <span className="text-xs text-slate-400">({allegati.length})</span>
        {!solaLettura && (
          <>
            <input ref={inputRef} type="file" accept={ACCETTATI} onChange={carica} className="hidden" data-testid="verbale-allegato-input" />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={carico}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50"
            >
              {carico ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Upload className="h-3.5 w-3.5" aria-hidden="true" />}
              Aggiungi
            </button>
          </>
        )}
      </div>

      {allegati.length > 0 ? (
        <ul className="space-y-1.5">
          {allegati.map((a) => (
            <li key={a._id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 dark:border-gray-700 dark:bg-gray-900/40">
              <FileText className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-700 dark:text-gray-200">{a.filename}</span>
              <span className="flex-shrink-0 text-xs text-slate-400">{misura(a.size)}</span>
              <a
                href={`${UPLOADS_BASE}${a.path}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Scarica ${a.filename}`}
                className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-md text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
              {!solaLettura && (
                <button
                  type="button"
                  onClick={() => setDaTogliere(a)}
                  aria-label={`Rimuovi ${a.filename}`}
                  className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs italic text-slate-400">Nessun allegato</p>
      )}

      {!solaLettura && (
        <p className="mt-2 text-xs text-slate-500 dark:text-gray-400">
          PDF, Word (.docx), Excel (.xlsx), foto JPEG, PNG, WebP o HEIC, fino a 25 MB ciascuno.
        </p>
      )}

      <ConfirmDialog
        isOpen={!!daTogliere}
        onClose={() => setDaTogliere(null)}
        onConfirm={() => { if (daTogliere) togli(daTogliere); }}
        title="Rimuovere l'allegato?"
        message={daTogliere ? `«${daTogliere.filename}» non sarà più allegato al verbale.` : ''}
        confirmText="Rimuovi"
        type="danger"
      />
    </section>
  );
};

export default VerbaleAllegati;
